import React from 'react';
import { Card, Button, Grid } from '../components/BaseComponents';
import { SubscribeButton } from '../components/SubscribeButton';
import { useUser } from '../contexts/UserContext';
import { Radar } from 'react-chartjs-2';
import '../components/charts/ChartRegistry'; // Import to ensure chart components are registered
import { samplePerformanceData, demoQuestions } from '../data/landingPageSamples';
import { DemoQuestionWidget } from '../components/DemoQuestionWidget';
import { FeedbackPrompt } from '../components/BetaFeedback';
import { RiskReversal } from '../components/RiskReversal';
import { isFreeBeta } from '../utils/quizAccess';

/**
 * LandingPage - Welcome page for new users to encourage sign up
 * 
 * Features:
 * - Hero section highlighting math skill improvement for children
 * - Feature showcase with benefits
 * - Call-to-action buttons for sign up
 * - Social proof elements
 * - Responsive design following existing patterns
 */
export const LandingPage: React.FC = () => {
  const { currentUser, needsSubscription, openSignupModal } = useUser();
  const freeBeta = isFreeBeta();

  // Chart data for hero section
  const heroChartData = {
    labels: samplePerformanceData.labels,
    datasets: [
      {
        label: samplePerformanceData.title,
        data: samplePerformanceData.data,
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(54, 162, 235, 1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(54, 162, 235, 1)',
      },
    ],
  };

  const heroChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1,
    layout: {
      padding: 0
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
    },
    scales: {
      r: {
        min: 0,
        max: 100,
        ticks: {
          stepSize: 25,
          font: {
            size: 10,
          },
        },
        pointLabels: {
          font: {
            size: 11,
          },
        },
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-ivory to-warm-gray pb-20 lg:pb-0">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-ivory via-white to-warm-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Column - Hero Content */}
            <div className="text-center lg:text-left">
              <h1 className="text-4xl tracking-tight font-extrabold text-midnight-blue sm:text-5xl md:text-6xl animate-scale-in">
                <span className="block">Maths Practice for</span>
                <span className="block text-golden-yellow animate-warm-pulse">Years 7 &amp; 8</span>
                <span className="block text-2xl sm:text-3xl md:text-4xl mt-2">Explore the ThinkSphere Beta</span>
              </h1>
              <p className="mt-6 max-w-md mx-auto lg:mx-0 text-base text-cool-gray sm:text-lg md:mt-8 md:text-xl animate-scale-in font-semibold">
                Practise NSW Stage 4 maths topics, review quiz results and worked solutions, and track your progress. Content is still being added.
                {freeBeta && <span className="block mt-3">Free during the beta, with no payments or credit card required. Try it and help us improve with your feedback.</span>}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                <Button
                  size="lg"
                  className="flex items-center justify-center px-8 py-3 text-base hover-bounce animate-gentle-glow"
                  onClick={openSignupModal}
                >
                  {freeBeta ? "Join the Free Beta" : "Start Free 30-Day Trial"}
                  <svg className="ml-2 -mr-1 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="flex items-center justify-center px-8 py-3 text-base"
                  onClick={() => {
                    // Scroll to interactive demo section
                    const demoSection = document.querySelector('.demo-section');
                    if (demoSection) {
                      demoSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  See How It Works
                </Button>
              </div>
              <div className="mt-6 flex flex-wrap justify-center lg:justify-start items-center gap-4 text-sm text-cool-gray">
                <div className="flex items-center animate-scale-in hover-gentle-scale">
                  <svg className="w-5 h-5 text-mint-green mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  No credit card required
                </div>
                <div className="flex items-center animate-scale-in hover-gentle-scale">
                  <svg className="w-5 h-5 text-mint-green mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Years 7 &amp; 8 available now
                </div>
                <div className="flex items-center animate-scale-in hover-gentle-scale">
                  <svg className="w-5 h-5 text-mint-green mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  More year levels coming soon
                </div>
              </div>
            </div>

            {/* Right Column - Analytics Preview */}
            {/* Mobile Version - Compact */}
            <div className="block lg:hidden animate-scale-in">
              <div className="relative">
                <div className="bg-white rounded-lg shadow-lg p-4 overflow-hidden">
                  <div className="mb-4 text-center">
                    <h3 className="text-lg font-bold text-gray-900">Explore Your Progress</h3>
                    <p className="text-xs text-gray-600 mt-1">Illustrative preview with sample data, not actual student results</p>
                  </div>
                  <div className="py-2 flex justify-center items-center">
                    <div className="w-[280px] h-[280px] relative flex items-center justify-center">
                      <Radar data={heroChartData} options={heroChartOptions} />
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-3 text-center border-t pt-4">
                    <div>
                      <div className="text-xl font-bold text-blue-600">82%</div>
                      <div className="text-xs text-gray-600">Avg Score</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-green-600">47</div>
                      <div className="text-xs text-gray-600">Quizzes</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-purple-600">12</div>
                      <div className="text-xs text-gray-600">Day Streak</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop Version - Full Size */}
            <div className="hidden lg:block animate-scale-in">
              <div className="relative">
                <div className="bg-white rounded-xl shadow-xl p-8 overflow-hidden">
                  <div className="mb-6 text-center">
                    <h3 className="text-xl font-bold text-gray-900">Explore Your Progress</h3>
                    <p className="text-sm text-gray-600 mt-2">Illustrative preview with sample data, not actual student results</p>
                  </div>
                  <div className="py-4 flex justify-center items-center">
                    <div className="w-[400px] h-[400px] relative flex items-center justify-center">
                      <Radar data={heroChartData} options={heroChartOptions} />
                    </div>
                  </div>
                  <div className="mt-6 grid grid-cols-3 gap-4 text-center border-t pt-6">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">82%</div>
                      <div className="text-xs text-gray-600">Avg Score</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">47</div>
                      <div className="text-xs text-gray-600">Quizzes</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">12</div>
                      <div className="text-xs text-gray-600">Day Streak</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interactive Demo Section - Phase 2 */}
      <div className="demo-section py-16 bg-gradient-to-b from-warm-gray to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <span className="bg-golden-yellow text-white px-4 py-2 rounded-full text-sm font-bold shadow-md">
                Try It Yourself - No Signup Required
              </span>
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-4">
              Try a Few Sample Questions
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Explore this interactive demo with sample questions, answer feedback, and worked solutions.
            </p>
          </div>

          <DemoQuestionWidget
            questions={demoQuestions}
            onComplete={() => {
              // Scroll to final CTA when demo is completed
              const ctaSection = document.querySelector('.final-cta-section');
              if (ctaSection) {
                ctaSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }}
          />

          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">
              Sign up to explore the available Year 7 and Year 8 quizzes. Topics without questions are marked Coming Soon.
            </p>
            <Button
              size="lg"
              className="px-8 py-3 shadow-lg hover:shadow-xl transition-shadow"
              onClick={openSignupModal}
            >
              See Your Full Progress Dashboard - {freeBeta ? "Join the Free Beta" : "Start Free Trial"}
            </Button>
          </div>
        </div>
      </div>

      {/* Risk Reversal Section */}
      {!freeBeta && <RiskReversal />}

      {/* Features Section */}
      <div className="features-section py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Practice, Review and Track Progress
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-500">
              Explore the beta and tell us what works well and what needs improvement.
            </p>
          </div>

          <Grid cols={3} className="mt-16">
            <Card className="text-center">
              <div className="mx-auto icon-circle-lg bg-indigo-100 mb-4">
                <svg className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0121 12c0 5.523-4.477 10-10 10S1 17.523 1 12 5.477 2 11 2c2.231 0 4.29.661 6.02 1.799m-6.02 9.201l-2-2-4 4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Review Your Answers</h3>
              <p className="text-gray-600 mb-3">
                Check your quiz results and use explanations and worked solutions to review your answers.
              </p>
            </Card>

            <Card className="text-center">
              <div className="mx-auto icon-circle-lg bg-green-100 mb-4">
                <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose Your Practice Topics</h3>
              <p className="text-gray-600 mb-3">
                Browse Year 7 and Year 8 syllabus topics and choose from the quizzes currently available.
              </p>
            </Card>

            <Card className="text-center">
              <div className="mx-auto icon-circle-lg bg-purple-100 mb-4">
                <svg className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">See Your Quiz Progress</h3>
              <p className="text-gray-600 mb-3">
                Use your dashboard and quiz history to review scores and performance across the topics you have practised.
              </p>
            </Card>
          </Grid>
        </div>
      </div>


      {/* Beta availability and feedback */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-gray-900">What’s Available in the Beta?</h2>
          <p className="mt-4 text-xl text-gray-600">
            Year 7 and Year 8 maths quizzes are available now. Other year levels are coming soon.
            Coverage is incomplete, and we are still adding and reviewing questions.
          </p>
          <p className="mt-4 text-gray-600">
            Your feedback helps us improve the questions and the experience.
            If you spot a problem with a question, use “Flag for Review” on your quiz results.
          </p>
          <div className="mt-6"><FeedbackPrompt /></div>
        </div>
      </div>

      {/* Subscription Call to Action for logged-in users */}
      {!freeBeta && currentUser && needsSubscription() && (
        <div className="py-16 mx-4 sm:mx-6 lg:mx-8">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow-medium text-center p-8 sm:p-12 border border-blue-200">
            <h2 className="text-3xl font-extrabold text-blue-900 sm:text-4xl animate-scale-in">
              Welcome back! Your trial has expired
            </h2>
            <p className="mt-4 text-blue-800 text-lg">
              Continue your math journey with full access to all quizzes and features.
            </p>
            <div className="mt-8">
              <SubscribeButton variant="large" className="shadow-soft hover:shadow-medium" />
            </div>
            <p className="mt-4 text-blue-700 text-sm">
              Access the quizzes and features currently available.
            </p>
          </div>
        </div>
      )}

      {/* Call to Action for non-logged-in users */}
      {!currentUser && (
        <div className="final-cta-section py-16 mx-4 sm:mx-6 lg:mx-8">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-neutral-100 to-neutral-200 rounded-lg shadow-medium text-center p-8 sm:p-12">
            <h2 className="text-3xl font-extrabold text-text-primary sm:text-4xl animate-scale-in">
              Help Shape ThinkSphere
            </h2>
            <div className="mt-8">
              <Button
                variant="outline"
                size="lg"
                className="bg-primary-500 text-white hover:bg-primary-600 border-primary-500 hover:border-primary-600 px-8 py-3 text-lg font-semibold shadow-soft hover:shadow-medium"
                onClick={openSignupModal}
              >
                {freeBeta ? "Join the Free Beta Today" : "Start Your Free Trial Today"}
              </Button>
            </div>
            <p className="mt-4 text-text-secondary text-sm">
              Try the available quizzes and share your feedback. We are building ThinkSphere with input from students and families.
            </p>
          </div>
        </div>
      )}

      {/* Sticky Mobile CTA Bar - Only visible on mobile */}
      {!currentUser && (
        <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-gradient-to-r from-primary-500 to-primary-600 shadow-2xl border-t border-primary-700">
          <div className="px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm">Ready to get started?</p>
              <p className="text-primary-100 text-xs truncate">{freeBeta ? "Free during the beta • No credit card" : "30-day free trial • No credit card"}</p>
            </div>
            <Button
              size="lg"
              className="flex-shrink-0 bg-white !text-primary-600 hover:bg-primary-50 hover:!text-primary-700 font-bold px-6 py-3 shadow-lg min-h-[48px]"
              onClick={openSignupModal}
            >
              {freeBeta ? "Join Beta" : "Start Trial"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;